import React, { useState, useEffect } from "react";
import { Key, Plus, Edit, Trash2, ToggleLeft, ToggleRight, X, Save, Check, Server, Calendar } from "lucide-react";

const GeminiKeys = () => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [editingKey, setEditingKey] = useState(null);
  const [newKey, setNewKey] = useState({
    name: "",
    key: "",
    isActive: true
  });

  // Fetch all keys
  useEffect(() => {
    const fetchKeys = async () => {
      try {
        setLoading(true);
        const response = await fetch('https://allinone-clone-7d5d795e98ce.herokuapp.com/api/gemini-keys');
        const result = await response.json();
        
        if (result.success) {
          setKeys(result.data);
        } else {
          throw new Error(result.message || "Failed to fetch keys");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchKeys();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewKey(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  // Handle edit form input changes
  const handleEditInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditingKey(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  // Add a new key
  const handleAddKey = async (e) => {
    e.preventDefault();
    
    if (!newKey.name.trim() || !newKey.key.trim()) {
      setMessage({ text: "Name and key are required", type: "error" });
      return;
    }
    
    try {
      const response = await fetch('https://allinone-clone-7d5d795e98ce.herokuapp.com/api/gemini-keys', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newKey)
      });
      
      const result = await response.json();
      
      if (result.success) {
        setKeys(prev => [result.data, ...prev]);
        setNewKey({ name: "", key: "", isActive: true });
        setMessage({ text: "API key added successfully!", type: "success" });
      } else {
        throw new Error(result.message || "Failed to add key");
      }
    } catch (err) {
      setMessage({ text: `Error: ${err.message}`, type: "error" });
    }
  };

  // Update an existing key
  const handleUpdateKey = async (e) => {
    e.preventDefault();
    
    if (!editingKey.name.trim() || !editingKey.key.trim()) {
      setMessage({ text: "Name and key are required", type: "error" });
      return;
    }
    
    try {
      const response = await fetch(`https://allinone-clone-7d5d795e98ce.herokuapp.com/api/gemini-keys/${editingKey._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editingKey)
      });
      
      const result = await response.json();
      
      if (result.success) {
        setKeys(prev => 
          prev.map(key => key._id === editingKey._id ? result.data : key)
        );
        setEditingKey(null);
        setMessage({ text: "API key updated successfully!", type: "success" });
      } else {
        throw new Error(result.message || "Failed to update key");
      }
    } catch (err) {
      setMessage({ text: `Error: ${err.message}`, type: "error" });
    }
  };

  // Delete a key
  const handleDeleteKey = async (id) => {
    if (!window.confirm("Are you sure you want to delete this API key?")) {
      return;
    }
    
    try {
      const response = await fetch(`https://allinone-clone-7d5d795e98ce.herokuapp.com/api/gemini-keys/${id}`, {
        method: 'DELETE'
      });
      
      const result = await response.json();
      
      if (result.success) {
        setKeys(prev => prev.filter(key => key._id !== id));
        setMessage({ text: "API key deleted successfully!", type: "success" });
      } else {
        throw new Error(result.message || "Failed to delete key");
      }
    } catch (err) {
      setMessage({ text: `Error: ${err.message}`, type: "error" });
    }
  };

  // Toggle key active status
  const handleToggleStatus = async (id) => {
    try {
      const response = await fetch(`https://allinone-clone-7d5d795e98ce.herokuapp.com/api/gemini-keys/${id}/toggle`, {
        method: 'PATCH'
      });
      
      const result = await response.json();
      
      if (result.success) {
        setKeys(prev => 
          prev.map(key => key._id === id ? result.data : key)
        );
        setMessage({ 
          text: `API key ${result.data.isActive ? "activated" : "deactivated"} successfully!`, 
          type: "success" 
        });
      } else {
        throw new Error(result.message || "Failed to toggle key status");
      }
    } catch (err) {
      setMessage({ text: `Error: ${err.message}`, type: "error" });
    }
  };

  // Start editing a key
  const startEditing = (key) => {
    setEditingKey({ ...key });
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingKey(null);
  };

  // Format date for display
  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden"></span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger d-flex align-items-center m-4" role="alert">
        <X className="me-2" size={18} />
        <div>Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <h3 className="fw-bold">Gemini API Keys Management</h3>
        <div className="d-flex align-items-center">
            <span className="badge bg-primary rounded-pill px-3 py-2 fs-6">
                {keys.length} {keys.length === 1 ? 'Key' : 'Keys'}
            </span>
        </div>
      </div>
      
      {/* Notification Alert */}
      {message.text && (
        <div className={`alert ${message.type === "success" ? "alert-success" : "alert-danger"} alert-dismissible fade show mb-4`} role="alert">
          <div className="d-flex align-items-center">
            {message.type === "success" ? <Check className="me-2" size={18} /> : <X className="me-2" size={18} />}
            <div>{message.text}</div>
          </div>
          <button type="button" className="btn-close" onClick={() => setMessage({ text: "", type: "" })}></button>
        </div>
      )}
      
      <div className="row g-4">
        {/* Add New Key Form */}
        <div className="col-lg-5">
          <div className="card shadow-sm h-100">
            <div className="card-header bg-white fw-bold py-3 d-flex align-items-center gap-2">
              <Plus size={20} className="text-primary" /> Add New API Key
            </div>
            <div className="card-body">
              <form onSubmit={handleAddKey}>
                <div className="mb-3">
                  <label htmlFor="keyName" className="form-label small fw-bold">Key Name</label>
                  <input
                    type="text"
                    className="form-control"
                    id="keyName"
                    name="name"
                    value={newKey.name}
                    onChange={handleInputChange}
                    placeholder="e.g., Production Key"
                    required
                  />
                </div>
                
                <div className="mb-3">
                  <label htmlFor="apiKey" className="form-label small fw-bold">API Key</label>
                  <input
                    type="password"
                    className="form-control"
                    id="apiKey"
                    name="key"
                    value={newKey.key}
                    onChange={handleInputChange}
                    placeholder="Enter your Gemini API key"
                    required
                  />
                </div>
                
                <div className="mb-4 form-check form-switch d-flex align-items-center gap-2">
                  <input
                    className="form-check-input mt-0"
                    type="checkbox"
                    id="isActive"
                    name="isActive"
                    checked={newKey.isActive}
                    onChange={handleInputChange}
                  />
                  <label className="form-check-label small mb-0" htmlFor="isActive">
                    Active Status
                  </label>
                </div>
                
                <button
                  type="submit"
                  className="btn btn-primary w-100 d-flex align-items-center justify-content-center gap-2"
                >
                  <Plus size={18} /> Add Key
                </button>
              </form>
            </div>
          </div>
        </div>
        
        {/* Edit Key Form */}
        {editingKey && (
          <div className="col-lg-5">
            <div className="card shadow-sm h-100">
              <div className="card-header bg-white fw-bold py-3 d-flex align-items-center gap-2">
                <Edit size={20} className="text-warning" /> Edit API Key
              </div>
              <div className="card-body">
                <form onSubmit={handleUpdateKey}>
                  <div className="mb-3">
                    <label htmlFor="editKeyName" className="form-label small fw-bold">Key Name</label>
                    <input
                      type="text"
                      className="form-control"
                      id="editKeyName"
                      name="name"
                      value={editingKey.name}
                      onChange={handleEditInputChange}
                      required
                    />
                  </div>
                  
                  <div className="mb-3">
                    <label htmlFor="editApiKey" className="form-label small fw-bold">API Key</label>
                    <input
                      type="password"
                      className="form-control"
                      id="editApiKey"
                      name="key"
                      value={editingKey.key}
                      onChange={handleEditInputChange}
                      required
                    />
                  </div>
                  
                  <div className="mb-4 form-check form-switch d-flex align-items-center gap-2">
                    <input
                      className="form-check-input mt-0"
                      type="checkbox"
                      id="editIsActive"
                      name="isActive"
                      checked={editingKey.isActive}
                      onChange={handleEditInputChange}
                    />
                    <label className="form-check-label small mb-0" htmlFor="editIsActive">
                      Active Status
                    </label>
                  </div>
                  
                  <div className="d-flex gap-2">
                    <button
                      type="submit"
                      className="btn btn-warning flex-fill d-flex align-items-center justify-content-center gap-2 text-white"
                    >
                      <Save size={18} /> Update Key
                    </button>
                    <button
                      type="button"
                      onClick={cancelEditing}
                      className="btn btn-outline-secondary flex-fill d-flex align-items-center justify-content-center gap-2"
                    >
                      <X size={18} /> Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Keys List */}
      <div className="mt-4">
        <div className="card shadow-sm">
          <div className="card-header bg-white fw-bold py-3 d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-2">
              <Server size={20} className="text-primary" /> Your API Keys
            </div>
          </div>
          <div className="card-body p-0">
            {keys.length === 0 ? (
              <div className="text-center py-5">
                <div className="mb-3 text-muted">
                  <Key size={48} />
                </div>
                <h6 className="text-muted mb-2">No API keys found</h6>
                <p className="text-muted small mb-0">Add your first key using the form above</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="text-white border-bottom border-secondary">
                    <tr>
                      <th scope="col" className="ps-4 small text-white text-uppercase">Name</th>
                      <th scope="col" className="small text-white text-uppercase">Key</th>
                      <th scope="col" className="small text-white text-uppercase">Status</th>
                      <th scope="col" className="small text-white text-uppercase">Created</th>
                      <th scope="col" className="text-end pe-4 small text-white text-uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {keys.map((key) => (
                      <tr key={key._id}>
                        <td className="ps-4">
                          <div className="fw-medium text-white">{key.name}</div>
                        </td>
                        <td>
                          <code className="bg-light px-2 py-1 rounded small">
                            {key.key.substring(0, 8)}...{key.key.substring(key.key.length - 4)}
                          </code>
                        </td>
                        <td>
                          <span className={`badge ${key.isActive ? 'bg-success' : 'bg-danger'} text-white`}>
                            {key.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="small text-muted">
                          <div className="d-flex align-items-center gap-1">
                            <Calendar size={14} />
                            {formatDate(key.createdAt)}
                          </div>
                        </td>
                        <td className="text-end pe-4">
                          <div className="btn-group" role="group">
                            <button
                              onClick={() => handleToggleStatus(key._id)}
                              className={`btn btn-sm ${key.isActive ? 'btn-warning' : 'btn-success'} text-white`}
                              title={key.isActive ? "Disable" : "Enable"}
                            >
                              {key.isActive ? <ToggleLeft size={16} /> : <ToggleRight size={16} />}
                            </button>
                            <button
                              onClick={() => startEditing(key)}
                              className="btn btn-sm btn-outline-primary"
                              title="Edit"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              onClick={() => handleDeleteKey(key._id)}
                              className="btn btn-sm btn-outline-danger"
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeminiKeys;